import type { TaskTypes } from './ApiTask';
import type { Coordinates, TimeWindow } from './CommonTypes';

/**
 * Enum of supported assignment statuses
 * @category API Types
 */
export enum AssignmentStatus {
    ASSIGNED,
    IN_PROGRESS,
    COMPLETED,
    CANCELED
}

/**
 * Enum of supported assignment delay codes. Enum value starts at -1 (early)
 * @category API Types
 */
export enum AssignmentDelay {
    EARLY = -1,
    ON_TIME,
    AT_RISK,
    LATE
}

/**
 * Type for assignment props
 * @category API Types
 */
interface AssignmentProps {
    /**
     * The assignment time windows
     */
    window: Array<TimeWindow>;
    /**
     * The assignment inventory
     * @todo write TS definitions for inventory items
     */
    inventory?: Array<Record<string, unknown>>;
}

export interface AssignmentStatusChange {
    /**
     * The assignment status to change to
     *
     * Updating to `assigned` (AssignmentStatus.ASSIGNED)
     * + will unset assignment `startServiceAt` value, if set
     * + will unset assignment `completedAt` value, if set
     *
     * Updating to `in-progress` (AssignmentStatus.IN_PROGRESS)
     * + updates assignment `startServiceAt` value
     * + will unset assignment `completedAt` value, if set
     *
     * Updating to `completed` (AssignmentStatus.COMPLETED)
     * + future assignment status changes not allowed after update to `completed`
     * + updates assignment `completedAt` value
     * + updating from `assigned` requires an update to `in-progress` first
     *
     * Updating to `canceled` (AssignmentStatus.CANCELED)
     * + future assignment status changes not allowed after update to `canceled`
     * + updates assignment `completedAt` value
     * + updating from `assigned` requires an update to `in-progress` first
     */
    status: AssignmentStatus;

    /**
     * The assignment status change timestamp
     *
     * Important
     * + for status `assigned`: timestamp will be used for assignment `updatedAt` value
     * + for status `in-progress`: timestamp will be used for assignment `startServiceAt` value
     * + for status `completed`: timestamp will be used for assignment `completedAt` value
     * + for status `canceled`: timestamp will be used for assignment `completedAt` value
     */
    timestamp: string;
}

/**
 * Type for API assignments
 * @category API Types
 */
export interface ApiAssignment {
    /**
     * Id of the assignment
     */
    id: string;
    /**
     * Task associated to this assignment
     */
    task: string;
    /**
     * Driver associated to this assignment
     */
    driver: string;
    /**
     * The assignment status
     */
    status: AssignmentStatus;
    /**
     * The assignment delay
     */
    delay: AssignmentDelay;
    /**
     * The assignment type
     */
    type: TaskTypes;
    /**
     * The assignment location
     */
    location: Coordinates;
    /**
     * The assignment name
     */
    name?: string;
    /**
     * The assignment arrival time
     */
    arrivalTime?: string;
    /**
     * The assignment completed at time
     */
    completedAt?: string;
    /**
     * The assignment start service at time
     */
    startServiceAt?: string;
    /**
     * The assignment props
     */
    props: AssignmentProps;
    /**
     * The revised assignment start service at time
     */
    revisedStartServiceAt?: string;
    /**
     * The revised assignment completed at time
     */
    revisedCompletedAt?: string;
    /**
     * Assignment level photo URLS
     */
    photoUrls?: Array<string>;
    /**
     * Assignment level signature URL
     */
    signatureUrl?: string;

    /**
     * When the assignment was created
     */
    createdAt: string;

    /**
     * The assignment settings
     */
    settings?: Record<string, boolean | string | number>;
}
